<template>
  <div>
    <div v-if="$apollo.loading">
      <spinner />
    </div>
    <div v-else class="container">
      <h1 class="screen-title">Organizations</h1>
      <div v-if="organizationCreated" class="organization-created-wrapper">
        <div class="organization-created">
          <img src="/assets/done.svg" alt="organization created" class="organization-created-img">
          <p class="organization-created-msg">Organization successfully created</p>
        </div>
        <div class="close-wrapper" @click="closeMsg()">
          <p class="close-msg">Close</p>
          <v-icon color="#55A35D" class="close-icon">close</v-icon>
        </div>
      </div>
      <div class="organization-container">
        <div
          v-for="organization in organizations"
          :key="organization.id"
          class="organization-container"
        >
          <organization-card
            :organization="organization"
            :is-valid="!isOrganizationInvalid(organization)"
            @select="selectOrganization($event)"
            @createEvent="createEvent($event)"
            @toggleDefault="toggleDefault($event)"
          />
        </div>
        <div class="add-new-organization-button" @click="goTo(routes.organizer.createOrganization)">
          ADD NEW ORGANIZATION
        </div>
      </div>
      <initial-organization-update-modal
        v-if="showModal"
        :initial-values="form"
        :vs="$v"
        @submit="initialUpdateOrganization()"
        @closeModal="closeModal()"
      />
    </div>
    <simple-snackbar v-bind.sync="snackbar" />
    <demo-modal
      v-if="demoModal"
      :user="user"
      @close="demoModal = false"
    />
  </div>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators'
import { omit } from 'lodash'
import { GET_ORGANIZATIONS, UPDATE_ORGANIZATION } from '@/graphql/organization.graphql'
import { TOGGLE_DEFAULT_ORGANIZATION } from '@/graphql/userOrganization.graphql'
import { submitGuard } from '@/utils'
import { OrganizationType } from '@/types'

import organizationCard from './organization-card.vue'
import initialOrganizationUpdateModal from './initial-organization-update-modal.vue'
import simpleSnackbar from '../shared/snackbars/simple-snackbar.vue'
import DemoModal from '@/components/organizations/DemoModal.vue'
import { GET_LOGGED_USER } from '@/graphql/user.graphql'

export default {
  name: 'organizations',
  components: { DemoModal, organizationCard, initialOrganizationUpdateModal, simpleSnackbar },
  props: {
    organizationCreated: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    form: {},
    user: null,
    OrganizationType,
    organizations: [],
    showModal: false,
    snackbar: null,
    demoModal: false,
  }),
  validations() {
    return {
      form: {
        publicName: { required },
        email: { required, email },
        legalName: { required },
        OIB: { required },
        address: { required },
        IBAN: { required },
        VATObligor: { required },
      },
    }
  },
  methods: {
    closeMsg() {
      this.organizationCreated = false
    },
    isOrganizationInvalid(organization) {
      return (
        organization.publicName === null ||
        organization.email === null ||
        organization.legalName === null ||
        organization.slug === null ||
        organization.OIB === null ||
        organization.address === null ||
        organization.IBAN === null ||
        organization.VATObligor === null
      )
    },
    closeModal() {
      this.form = {}
      this.showModal = false
      this.$v.$reset()
    },
    selectOrganization(organization) {
      if (this.isOrganizationInvalid(organization)) {
        this.form = { ...omit(organization, ['liveEventsCount', 'userOrganization']) }
        this.showModal = true
      } else {
        if (organization.type === this.OrganizationType.THEATER) {
          this.goTo(this.routes.main.theaterOrganizationSidebar, {
            organizationSlug: organization.slug,
          })
        } else if (organization.type === this.OrganizationType.SPORT_ORGANIZATION) {
          this.goTo(this.routes.main.sportOrganizationSidebar, {
            organizationSlug: organization.slug,
          })
        } else {
          this.goTo(this.routes.main.organizationSidebar, {
            organizationSlug: organization.slug,
          })
        }
      }
    },
    createEvent(organization) {
      if (this.isOrganizationInvalid(organization)) {
        this.form = { ...omit(organization, ['liveEventsCount', 'userOrganization']) }
        this.showModal = true
      } else {
        this.goTo(
          organization.type === OrganizationType.REGULAR
            ? this.routes.organization.organizationAddEvent
            : organization.type === OrganizationType.THEATER
              ? this.routes.theaterOrganization.theaterOrganizationAddEvent
              : this.routes.sportOrganization.sportOrganizationAddEvent,
          {
            organizationSlug: organization.slug,
          },
        )
      }
    },
    async initialUpdateOrganization() {
      this.$v.form.$touch()

      if (this.$v.form.$invalid === true) {
        this.snackbar = {
          open: true,
          messageProp: 'Fill in the required information.',
          color: 'error',
        }
      } else {
        await submitGuard(this, async () => {
          const { errors } = await this.$apollo.mutate({
            mutation: UPDATE_ORGANIZATION,
            variables: { organizationId: this.form.id, organizationInfo: omit(this.form, 'id') },
            refetchQueries: [{ query: GET_ORGANIZATIONS }],
          })

          if (errors) {
            this.snackbar = {
              open: true,
              messageProp: errors[0].message,
              color: 'error',
            }
          } else {
            this.snackbar = {
              open: true,
              messageProp: `Successfully updated ${this.form.publicName} organization!`,
              color: 'success',
            }

            this.closeModal()
          }
        })
      }
    },
    async toggleDefault(organization) {
      const wasDefault = organization.userOrganization.default
      const { errors } = await this.$apollo.mutate({
        mutation: TOGGLE_DEFAULT_ORGANIZATION,
        variables: { organizationSlug: organization.slug },
        refetchQueries: [{ query: GET_ORGANIZATIONS }],
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: errors[0].message,
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: wasDefault
            ? `Successfully removed ${organization.publicName} as default`
            : `Successfully set ${organization.publicName} as default`,
          color: 'success',
        }
      }
    },
  },
  apollo: {
    me: {
      query: GET_LOGGED_USER,
      async result({ data, loading }) {
        if (!loading && data) {
          const { me: user } = data
          this.user = user
          if (this.organizationCreated && !this.user.isVerifiedByCoreEvent) {
            this.demoModal = true
          }
        }
      },
    },
    getOrganizations: {
      query: GET_ORGANIZATIONS,
      result({ data, loading }) {
        if (!loading && data) {
          this.organizations = data.getOrganizations || []
        }
      },
    },
  },
}
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.5rem;
  max-width: 566px;
  padding: 0;
  margin-top: 2rem;
}

.screen-title {
  font-family: Raleway;
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  align-self: flex-start;
  padding: 0;
  margin: 0;
}

p {
  display: inline;
}

.organization-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 1.5rem;
  max-width: 566px;
}

.add-new-organization-button {
  padding: 12px 24px;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 120%;
  text-align: center;
  letter-spacing: -0.015em;
  text-transform: uppercase;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
  align-self: flex-start;
}

.add-new-organization-button:hover {
  cursor: pointer;
  background-color: #e6f1f8;
  border-radius: 24px;
}

.create-organization {
  width: 14rem;
  height: 14rem;
  background: #ffffff;
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 700;
  font-size: 1.125rem;
  text-align: center;
  color: #0277bd;
  border: 0.125rem dashed #cee4f2;
  display: flex;
  cursor: pointer;
  border-radius: 0.5rem;
}

.text {
  align-self: center;
  margin: 0 auto;
}

.add-button {
  height: 0.875rem;
  margin-right: 0.5rem;
  width: 0.875rem;
}

@media screen and (max-width: 800px) {
  .container {
    max-width: 480px;
  }

  .organization-container {
    max-width: 480px;
  }
}

@media screen and (max-width: 560px) {
  .organization-created-msg {
    font-size: 1rem !important;
    margin-top: 0.5rem;
  }

  .close-msg {
    display: none;
  }

  .close-icon {
    margin-top: 5px;
  }

  .organization-container {
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
}

.organization-created-wrapper {
  background: #eef6ef;
  border-radius: 0.75rem;
  color: #55a35d;
  display: flex;
  justify-content: space-between;
  max-width: 35.375rem;
  width: 100%;
}

@media screen and (max-width: 800px) {
  .organization-created-wrapper {
    max-width: 480px;
  }
}

.organization-created-msg {
  margin-left: 1rem;
  font-size: 1.5rem;
}

.organization-created {
  margin-left: 0.75rem;
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  color: #55a35d;
  display: flex;
  padding-top: 0.75rem;
}

.close-msg {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.25rem;
  cursor: pointer;
  color: #55a35d;
  margin-top: 0.95rem;
  margin-right: 0.5rem;
}

.organization-created-img {
  height: 1.875rem;
  width: 1.875rem;
  margin-top: 0.25rem;
}

.close-wrapper {
  padding-top: 0.85rem;
  margin-right: 0.75rem;
}

.close-wrapper:hover {
  cursor: pointer;
}
</style>
